<template>
	<div class="dashboard-layout">
		<div class="dashboard-header flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
			<div class="flex flex-column gap-1">
				<WTitle text="Dashboard" />
				<span class="text-sm text-color-secondary">
					Operational overview for the selected billing period
					<span v-if="lastUpdated"> · updated {{ lastUpdatedLabel }}</span>
				</span>
			</div>
			<div class="flex align-items-center gap-2 flex-wrap">
				<PDropdown
					:disabled="loading"
					:options="periodOptions"
					optionLabel="label"
					optionValue="value"
					placeholder="Select billing period"
					style="min-width: 14rem;"
					v-model="billingPeriodId"
					@change="onPeriodChange()"
				/>
				<div class="flex align-items-center gap-1">
					<PInputSwitch v-model="autoRefresh" />
					<span class="text-sm text-color-secondary">Auto (45s)</span>
				</div>
				<PButton
					v-if="canRunPpm"
					:disabled="!billingPeriodId || runningPpm || loading"
					:loading="runningPpm"
					icon="pi pi-chart-line"
					label="Run PPM"
					outlined
					rounded
					size="small"
					severity="success"
					@click="runPpm()"
				/>
				<PButton
					v-if="canRunTcm"
					:disabled="!billingPeriodId || runningTcm || loading"
					:loading="runningTcm"
					icon="pi pi-play"
					label="Run TCM"
					rounded
					size="small"
					severity="success"
					@click="runTcm()"
				/>
				<PButton
					:disabled="loading"
					icon="pi pi-refresh"
					label="Refresh"
					outlined
					rounded
					size="small"
					@click="refresh()"
				/>
			</div>
		</div>

		<div v-if="loading && !payload" class="dashboard-loading flex align-items-center justify-content-center py-6">
			<PProgressSpinner strokeWidth="4" style="width: 48px; height: 48px;" />
		</div>

		<template v-else-if="!billingPeriodId">
			<div class="dash-empty">
				<i class="mdi mdi-calendar-blank dash-empty-icon" />
				<p>No billing periods found. Create a period to get started.</p>
			</div>
		</template>

		<template v-else>
			<section v-if="payload?.kpis" class="dashboard-section mb-3">
				<DashKpiStrip :kpis="payload.kpis" />
				<div v-if="showMissingEngines" class="mt-2 text-sm text-color-secondary">
					<span v-if="!payload.kpis.tcm_available && canRunTcm">TCM has not been run for this period. </span>
					<span v-if="!payload.kpis.ppm_available && canRunPpm">PPM has not been run for this period.</span>
				</div>
			</section>

			<div class="dashboard-split mb-3">
				<section v-if="showActions" class="dashboard-panel">
					<DashActionQueue
						:actions="payload?.actions || null"
						:busyAction="actionBusy.action"
						:busyId="actionBusy.id"
						@approve-entry="approveEntry($event)"
						@approve-invoice="approveInvoice($event)"
						@reject-entry="rejectEntry($event)"
					/>
				</section>
				<section v-if="showOps" class="dashboard-panel">
					<DashOpsPanel
						:auditRecent="payload?.audit_recent || null"
						:busyAction="opsBusy"
						:dcs="payload?.dcs || null"
						:health="payload?.health || null"
						:mda="payload?.mda || null"
						:scheduler="payload?.scheduler || null"
						:telemetry="payload?.telemetry || null"
						@flush-telemetry="flushTelemetry()"
						@scheduler="schedulerControl($event)"
					/>
				</section>
			</div>

			<div class="dashboard-split mb-3">
				<section v-if="showEnergy" class="dashboard-panel">
					<DashEnergy :series="payload?.energy_series || []" />
				</section>
				<section v-if="showPr" class="dashboard-panel">
					<DashChartPr :hourly="payload?.ppm?.hourly || []" />
				</section>
			</div>

			<div class="dashboard-split mb-3">
				<section v-if="showPayments" class="dashboard-panel">
					<DashPayments :series="payload?.payments_series || []" />
				</section>
				<section v-if="showHeatmap" class="dashboard-panel">
					<DashValidityHeatmap :cells="payload?.validity_heatmap || []" />
				</section>
			</div>

			<div class="dashboard-split mb-3">
				<section v-if="showShortcuts" class="dashboard-panel">
					<DashShortcuts
						:billingPeriodId="billingPeriodId"
						:busy="shortcutBusy !== ''"
						:busyKey="shortcutBusy"
						:canGenerateInvoice="canGenerateInvoice"
						:canReports="canReports"
						:chargeBreakdown="payload?.charge_breakdown || []"
						:formulaSet="payload?.formula_set || null"
						:timeline="payload?.timeline || []"
						@generate-invoice="generateInvoice()"
						@report="generateReport($event)"
					/>
				</section>
				<section v-if="showCoefficients" class="dashboard-panel">
					<DashCoefficients :coefficients="payload?.coefficients || null" />
				</section>
			</div>

			<section ref="settlementPanel" v-if="showSettlement" class="dashboard-panel mb-3">
				<DashSettlement
					:approvingId="approvingInvoiceId"
					:billingPeriodId="billingPeriodId"
					:canApproveInvoice="canApproveInvoice"
					:canRunTcm="canRunTcm"
					:invoices="payload?.invoices || []"
					:runningTcm="runningTcm"
					:tcm="payload?.tcm || null"
					@approve-invoice="approveInvoice($event)"
					@run-tcm="runTcm()"
				/>
			</section>
		</template>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import store from '@/store'
	import Utils from '@/utils/Utils'
	import ReportsBroker from '@/brokers/ReportsBroker'
	import DashboardBroker, { DashboardPayload } from '@/brokers/DashboardBroker'
	import DashKpiStrip from '@/components/dashboard/DashKpiStrip.vue'
	import DashPayments from '@/components/dashboard/DashChartPayments.vue'
	import DashEnergy from '@/components/dashboard/DashChartEnergy.vue'
	import DashChartPr from '@/components/dashboard/DashChartPr.vue'
	import DashSettlement from '@/components/dashboard/DashSettlement.vue'
	import DashActionQueue from '@/components/dashboard/DashActionQueue.vue'
	import DashOpsPanel from '@/components/dashboard/DashOpsPanel.vue'
	import DashValidityHeatmap from '@/components/dashboard/DashValidityHeatmap.vue'
	import DashCoefficients from '@/components/dashboard/DashCoefficients.vue'
	import DashShortcuts from '@/components/dashboard/DashShortcuts.vue'

	const REFRESH_MS = 45000

	interface DashboardState {
		loading: boolean
		runningTcm: boolean
		runningPpm: boolean
		billingPeriodId: number | null
		payload: DashboardPayload | null
		autoRefresh: boolean
		lastUpdated: number | null
		refreshTimer: ReturnType<typeof setInterval> | null
		actionBusy: { id: number | null; action: string }
		opsBusy: string
		shortcutBusy: string
		approvingInvoiceId: number | null
	}

	export default defineComponent({
		name: 'DashboardView',
		components: {
			DashKpiStrip,
			DashPayments,
			DashEnergy,
			DashChartPr,
			DashSettlement,
			DashActionQueue,
			DashOpsPanel,
			DashValidityHeatmap,
			DashCoefficients,
			DashShortcuts,
		},

		data(): DashboardState {
			return {
				loading: false,
				runningTcm: false,
				runningPpm: false,
				billingPeriodId: null,
				payload: null,
				autoRefresh: true,
				lastUpdated: null,
				refreshTimer: null,
				actionBusy: { id: null, action: '' },
				opsBusy: '',
				shortcutBusy: '',
				approvingInvoiceId: null,
			}
		},

		computed: {
			canRunTcm(): boolean {
				return store.getters.can('tcm', 'run')
			},
			canRunPpm(): boolean {
				return store.getters.can('ppm', 'run')
			},
			canGenerateInvoice(): boolean {
				return store.getters.can('invoice', 'generate')
			},
			canApproveInvoice(): boolean {
				return store.getters.can('invoice', 'approve')
			},
			canReports(): boolean {
				return store.getters.can('reports', 'generate')
			},
			periodOptions(): Array<{ label: string; value: number }> {
				return (this.payload?.periods || []).map((p) => ({
					label: `${p.name}${p.is_trial ? ' (trial)' : ''} · ${p.status}`,
					value: p.id,
				}))
			},
			lastUpdatedLabel(): string {
				if (!this.lastUpdated) return ''
				return new Date(this.lastUpdated).toLocaleTimeString()
			},
			showEnergy(): boolean {
				return this.payload?.energy_series != null
			},
			showPr(): boolean {
				return this.payload?.ppm != null
			},
			showPayments(): boolean {
				return this.payload?.payments_series != null
			},
			showHeatmap(): boolean {
				return this.payload?.validity_heatmap != null
			},
			showSettlement(): boolean {
				return this.payload?.tcm != null || this.payload?.invoices != null
			},
			showActions(): boolean {
				return this.payload?.actions != null
			},
			showOps(): boolean {
				return Boolean(
					this.payload?.health
					|| this.payload?.mda
					|| this.payload?.telemetry
					|| this.payload?.scheduler
					|| this.payload?.dcs,
				)
			},
			showCoefficients(): boolean {
				return this.payload?.coefficients != null
			},
			showShortcuts(): boolean {
				return Boolean(
					this.canGenerateInvoice
					|| this.canReports
					|| this.payload?.formula_set
					|| this.payload?.timeline?.length
					|| this.payload?.charge_breakdown?.length,
				)
			},
			showMissingEngines(): boolean {
				const k = this.payload?.kpis
				if (!k) return false
				return (!k.tcm_available && this.canRunTcm) || (!k.ppm_available && this.canRunPpm)
			},
		},

		watch: {
			autoRefresh(on: boolean) {
				if (on) this.startAutoRefresh()
				else this.stopAutoRefresh()
			},
		},

		async mounted() {
			await this.refresh()
			if (this.autoRefresh) this.startAutoRefresh()
		},

		beforeUnmount() {
			this.stopAutoRefresh()
		},

		methods: {
			startAutoRefresh() {
				this.stopAutoRefresh()
				this.refreshTimer = setInterval(() => {
					if (!this.loading && !this.runningTcm && !this.runningPpm) {
						this.refresh({ silent: true })
					}
				}, REFRESH_MS)
			},
			stopAutoRefresh() {
				if (this.refreshTimer) {
					clearInterval(this.refreshTimer)
					this.refreshTimer = null
				}
			},
			async refresh(opts: { silent?: boolean } = {}) {
				if (!opts.silent) this.loading = true
				try {
					const data = await DashboardBroker.get(this.billingPeriodId)
					this.payload = data
					const selected = data.selected_period?.id ?? null
					if (selected != null) this.billingPeriodId = selected
					this.lastUpdated = Date.now()
				} catch (e: any) {
					if (!opts.silent) {
						Utils.showErrorMessage(e?.message || 'Failed to load dashboard.')
					}
				} finally {
					this.loading = false
				}
			},
			async onPeriodChange() {
				await this.refresh()
			},
			async runTcm() {
				if (!this.billingPeriodId || !this.canRunTcm) return
				this.runningTcm = true
				try {
					const result = await DashboardBroker.runTcm(this.billingPeriodId)
					Utils.showSuccessMessage(
						`TCM complete: TPn=${result.tpn}, ${result.valid_hour_count} valid hours.`,
					)
					await this.refresh()
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'TCM run failed.')
				} finally {
					this.runningTcm = false
				}
			},
			async runPpm() {
				if (!this.billingPeriodId || !this.canRunPpm) return
				this.runningPpm = true
				try {
					const result = await DashboardBroker.runPpm(this.billingPeriodId)
					Utils.showSuccessMessage(
						`PPM complete: ${result.sample_count} samples, `
						+ `${result.valid_hours} valid / ${result.invalid_hours} invalid hours.`,
					)
					await this.refresh()
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'PPM run failed.')
				} finally {
					this.runningPpm = false
				}
			},
			async approveEntry(id: number) {
				this.actionBusy = { id, action: 'approve' }
				try {
					await DashboardBroker.approveManualEntry(id)
					Utils.showSuccessMessage('Manual entry approved.')
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Approve failed.')
				} finally {
					this.actionBusy = { id: null, action: '' }
				}
			},
			async rejectEntry(id: number) {
				this.actionBusy = { id, action: 'reject' }
				try {
					await DashboardBroker.rejectManualEntry(id)
					Utils.showSuccessMessage('Manual entry rejected.')
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Reject failed.')
				} finally {
					this.actionBusy = { id: null, action: '' }
				}
			},
			async approveInvoice(id: number) {
				this.actionBusy = { id, action: 'approve-inv' }
				this.approvingInvoiceId = id
				try {
					await DashboardBroker.approveInvoice(id)
					Utils.showSuccessMessage('Invoice approved.')
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Invoice approval failed.')
				} finally {
					this.actionBusy = { id: null, action: '' }
					this.approvingInvoiceId = null
				}
			},
			async generateInvoice() {
				if (!this.billingPeriodId || !this.canGenerateInvoice) return
				this.shortcutBusy = 'invoice'
				try {
					const res = await DashboardBroker.generateInvoice(this.billingPeriodId)
					Utils.showSuccessMessage(
						`Draft invoice created${res?.invoice_filename ? ': ' + res.invoice_filename : '.'}`,
					)
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Invoice generation failed.')
				} finally {
					this.shortcutBusy = ''
				}
			},
			async generateReport(reportType: string) {
				if (!this.billingPeriodId || !this.canReports) return
				this.shortcutBusy = reportType
				try {
					const res = await DashboardBroker.generateReport(reportType, this.billingPeriodId)
					Utils.showSuccessMessage(`Report ready (${res?.row_count ?? 0} rows).`)
					if (res?.pdf_file) {
						window.open(ReportsBroker.downloadUrl(res.pdf_file), '_blank')
					} else if (res?.xlsx_file) {
						window.open(ReportsBroker.downloadUrl(res.xlsx_file), '_blank')
					}
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Report generation failed.')
				} finally {
					this.shortcutBusy = ''
				}
			},
			async flushTelemetry() {
				this.opsBusy = 'flush'
				try {
					await DashboardBroker.flushTelemetry()
					Utils.showSuccessMessage('Telemetry flush triggered.')
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Flush failed.')
				} finally {
					this.opsBusy = ''
				}
			},
			async schedulerControl(action: string) {
				this.opsBusy = action
				try {
					await DashboardBroker.schedulerControl(action)
					Utils.showSuccessMessage(`Scheduler: ${action}`)
					await this.refresh({ silent: true })
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Scheduler control failed.')
				} finally {
					this.opsBusy = ''
				}
			},
		},
	})
</script>

<style scoped>
	.dashboard-layout {
		padding: 0.25rem 0 1.5rem;
	}
	.dashboard-section,
	.dashboard-panel {
		border: 1px solid var(--surface-border);
		border-radius: 8px;
		padding: 1rem 1.15rem;
		background: var(--surface-card);
	}
	.dashboard-split {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 0.75rem;
	}
	.dash-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem 1rem;
		color: var(--text-color-secondary);
		gap: 0.75rem;
		border: 1px dashed var(--surface-border);
		border-radius: 8px;
	}
	.dash-empty-icon {
		font-size: 2.5rem;
		opacity: 0.5;
	}
</style>
